import { useState } from 'react';
import { api, can, getUser } from '../lib/api';
import { date, retention, shortList } from '../lib/format';
import {
  Card, Empty, ErrorNote, Field, Hash, Loading, Modal, PageHead, Pill, useResource, useToast,
} from '../components/ui';
import { RowActions } from '../components/RowActions';
import '../styles/children.css';

/**
 * CM-1 Purpose registry (wireframe W2).
 *
 * Purposes are the unit everything else hangs off, so this screen makes two
 * things impossible to miss: a purpose is versioned, and editing an active one
 * cuts a new version rather than rewriting what people already agreed to.
 */

interface Translation { name: string; description: string; dataCategories?: string[]; basisSubtype?: string }

interface LanguageSettings {
  languages: string[];
  catalogue: Array<{ code: string; endonym: string; english: string; interfacePack: boolean }>;
}

interface LawfulBasis { code: string; label: string; basis: 'consent' | 'legitimate_use' }

/** Which of the tenant's languages a purpose has complete text in. */
function coverage(p: PurposeVersion, languages: string[]): { done: string[]; missing: string[] } {
  const done: string[] = [];
  const missing: string[] = [];
  for (const code of languages) {
    if (code === 'en') { done.push(code); continue; }
    const t = p.translations?.[code];
    (t && t.name && t.description ? done : missing).push(code);
  }
  return { done, missing };
}

interface PurposeVersion {
  version: number;
  name: string;
  description: string;
  dataCategories: string[];
  basis: 'consent' | 'legitimate_use';
  basisSubtype: string | null;
  retentionDays: number | null;
  processors: string[];
  requiredForService: boolean;
  translations: Record<string, Translation>;
  childRule?: ChildRule;
  childExemption?: string | null;
  channels?: string[];
  contentHash: string;
  createdAt: string;
}

type ChildRule = 'guardian' | 'not_for_children' | 'exempt';

const CHILD_RULE_OPTIONS: Array<[ChildRule, string]> = [
  ['guardian', 'Through a verified parent or guardian'],
  ['not_for_children', 'Not offered to children (tracking, monitoring or targeted advertising)'],
  ['exempt', 'Exempt under the Fourth Schedule'],
];

/** Fourth Schedule items (Rule 12): Part A by class of fiduciary, Part B by purpose. */
const CHILD_EXEMPTIONS: Array<[string, string]> = [
  ['A1', 'Clinical or mental health establishment, healthcare professional (health services)'],
  ['A2', 'Allied healthcare professional (treatment or referral plan)'],
  ['A3', 'Educational institution (educational activities, safety)'],
  ['A4', 'Creche or day care (safety)'],
  ['A5', 'Transport for an institution, creche or day care (location during travel)'],
  ['B1', "Duty under law in the child's interest"],
  ['B2', 'Subsidy, benefit, service, certificate, licence or permit under S.7(b)'],
  ['B3', 'Email account, email only'],
  ['B4', 'Real-time location for safety'],
  ['B5', 'Keeping harmful content and advertising away from the child'],
  ['B6', 'Confirming the person is not a child, Rule 10 checks'],
];

function childSuffix(v: PurposeVersion): string {
  if (v.childRule === 'not_for_children') return ' · Not for children';
  if (v.childRule === 'exempt') return ` · Exempt${v.childExemption ? ` (${v.childExemption})` : ''}`;
  return '';
}

interface Purpose {
  id: string;
  key: string;
  currentVersion: number;
  status: 'draft' | 'active' | 'retired';
  createdAt: string;
  current: PurposeVersion;
  /** Tracking, behavioural monitoring or targeted advertising (S.9(3)). */
  tracking?: boolean;
  /** Processing stops within this many hours of a withdrawal; null uses the organisation default. */
  withdrawalStopHours?: number | null;
  dataItems?: Array<{ id: string; name: string; sensitivity: 'low' | 'medium' | 'high'; reviewed: boolean }>;
}

const EMPTY: PurposeVersion = {
  version: 1,
  name: '',
  description: '',
  dataCategories: [],
  basis: 'consent',
  basisSubtype: null,
  retentionDays: null,
  processors: [],
  requiredForService: false,
  translations: {},
  childRule: 'guardian',
  childExemption: null,
  channels: [],
  contentHash: '',
  createdAt: '',
};

const CHANNEL_OPTIONS: Array<[string, string]> = [['email', 'Email'], ['sms', 'SMS'], ['whatsapp', 'WhatsApp'], ['phone', 'Phone call'], ['post', 'Post'], ['push', 'App notification']];

export default function Purposes() {
  const user = getUser();
  const writable = can(user, 'purposes:edit');
  const toast = useToast();
  const [editing, setEditing] = useState<Purpose | 'new' | null>(null);
  const [retiring, setRetiring] = useState<Purpose | null>(null);

  const { data, error, loading, reload } = useResource<{ purposes: Purpose[] }>(
    () => api.get('/v1/purposes'),
  );
  const langs = useResource<LanguageSettings>(() => api.get('/v1/settings/languages'));
  const offered = langs.data?.languages ?? ['en'];
  const endonym = (code: string) => langs.data?.catalogue.find((l) => l.code === code)?.endonym ?? code;

  // DD-3: which systems hold each purpose's data, read from the discovery stack
  // when this deployment is connected to one.
  const bridge = useResource<{
    configured: boolean;
    purposes: Array<{ purposeId: string; systems: Array<{ system: string; entity: string; datasets: string[] }> }>;
  }>(() => api.get('/v1/bridge/systems'));
  const systemsFor = (id: string) => bridge.data?.purposes.find((x) => x.purposeId === id)?.systems ?? [];

  async function syncBridge() {
    try {
      const res = await api.post<{ configured: boolean; upserted?: number }>('/v1/bridge/sync');
      toast(res.configured ? `${res.upserted} purposes sent to Discovery.` : 'Discovery is not connected.', res.configured ? 'ok' : 'bad');
      bridge.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function activate(p: Purpose) {
    try {
      await api.post(`/v1/purposes/${p.id}/activate`);
      toast(`"${p.current.name}" activated.`, 'ok');
      reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <>
      <PageHead
        title="Purposes"
      >
        {writable && bridge.data?.configured && <button onClick={syncBridge}>Send to Discovery</button>}
        {writable && <button className="primary" onClick={() => setEditing('new')}>New purpose</button>}
      </PageHead>

      <ErrorNote error={error} />
      {loading && !data && <Loading what="Loading purposes" />}

      {data && (data.purposes.length === 0 ? (
        <Empty>No purposes.</Empty>
      ) : (
        <Card>
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Purpose</th>
                  <th>Basis</th>
                  <th>Data categories</th>
                  <th>Retention</th>
                  <th>Languages</th>
                  {bridge.data?.configured && <th>Systems holding this data</th>}
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.purposes.map((p) => (
                  <tr key={p.id}>
                    <td title={p.current.description}>
                      <strong>{p.current.name}</strong>
                      {p.current.requiredForService && <div className="small muted">Needed to provide the service</div>}
                    </td>
                    <td>
                      <span title={p.current.basisSubtype ?? undefined}>
                        {p.current.basis === 'consent' ? 'Consent (S.6)' : `Legitimate use ${p.current.basisSubtype?.match(/S\.7\([a-i]\)/)?.[0] ?? '(S.7)'}`}
                      </span>
                      {childSuffix(p.current)}{p.current.channels?.length ? ` · ${p.current.channels.length} channels` : ""}
                    </td>
                    <td className="text-list">
                      {shortList(p.current.dataCategories, 3)}
                      {p.dataItems?.some((i) => i.sensitivity === 'high') && <> <Pill tone="bad">High sensitivity</Pill></>}
                    </td>
                    <td className="small">{retention(p.current.retentionDays)}</td>
                    <td className="small">
                      {(() => {
                        const c = coverage(p.current, offered);
                        return (
                          <span title={[...c.done, ...c.missing].map(endonym).join(', ')}>
                            {c.done.join(', ')}
                            {c.missing.length > 0 && <span className="bad-text"> · {c.missing.length} missing</span>}
                          </span>
                        );
                      })()}
                    </td>
                    {bridge.data?.configured && (
                      <td className="small">
                        {systemsFor(p.id).length === 0
                          ? <span className="muted">None mapped</span>
                          : systemsFor(p.id).map((x) => (
                            <div key={x.system} title={x.datasets.join(', ')}>
                              {x.system} <span className="muted">· {x.entity} · {x.datasets.length} dataset{x.datasets.length === 1 ? '' : 's'}</span>
                            </div>
                          ))}
                      </td>
                    )}
                    <td>
                      {p.status === 'active' ? <span className="muted small">Active</span> : <Pill tone={p.status === 'draft' ? 'warn' : undefined}>{p.status === 'draft' ? 'Draft' : 'Retired'}</Pill>}
                    </td>
                    <td className="right" style={{ whiteSpace: 'nowrap' }}>
                      {writable && p.status !== 'retired' && (
                        <RowActions
                          primary={p.status === 'draft'
                            ? <button className="small primary" onClick={() => activate(p)}>Activate</button>
                            : <button className="small" onClick={() => setEditing(p)}>Edit</button>}
                          more={[
                            ...(p.status === 'draft' ? [{ label: 'Edit', onClick: () => setEditing(p) }] : []),
                            ...(p.status === 'active' ? [{ label: 'Retire', onClick: () => setRetiring(p), danger: true }] : []),
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {editing && (
        <PurposeEditor
          purpose={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => { setEditing(null); toast(message, 'ok'); reload(); }}
        />
      )}

      {retiring && (
        <RetireDialog
          purpose={retiring}
          onClose={() => setRetiring(null)}
          onDone={() => { setRetiring(null); toast('Purpose retired.', 'ok'); reload(); }}
        />
      )}
    </>
  );
}

function PurposeEditor({
  purpose, onClose, onSaved,
}: { purpose: Purpose | null; onClose: () => void; onSaved: (message: string) => void }) {
  const [form, setForm] = useState<PurposeVersion>(purpose?.current ?? EMPTY);
  const [translations, setTranslations] = useState<Record<string, Translation>>(purpose?.current.translations ?? {});
  const [lang, setLang] = useState<string | null>(null);
  const [tracking, setTracking] = useState<boolean>(purpose?.tracking ?? false);
  const [stopHours, setStopHours] = useState<number | null>(purpose?.withdrawalStopHours ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isActive = purpose?.status === 'active';

  const settings = useResource<LanguageSettings>(() => api.get('/v1/settings/languages'));
  const bases = useResource<{ bases: LawfulBasis[] }>(() => api.get('/v1/settings/lawful-bases'));
  const others = (settings.data?.languages ?? []).filter((c) => c !== 'en');
  const catalogue = settings.data?.catalogue ?? [];
  const info = (code: string) => catalogue.find((l) => l.code === code);
  const current = lang ?? others[0] ?? null;

  const set = <K extends keyof PurposeVersion>(key: K, value: PurposeVersion[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const setT = (code: string, patch: Partial<Translation>) =>
    setTranslations((t) => {
      const base: Translation = t[code] ?? { name: '', description: '' };
      return { ...t, [code]: { ...base, ...patch } };
    });

  // One select for the whole lawful-basis list. Consent is S.6; each S.7
  // clause is its own entry, stored as "S.7(x) — label".
  const basisValue = form.basis === 'consent' ? 'S.6' : (form.basisSubtype?.match(/^S\.7\([a-i]\)/)?.[0] ?? '');
  const pickBasis = (code: string) => {
    const b = bases.data?.bases.find((x) => x.code === code);
    if (!b) return;
    setForm((f) => ({ ...f, basis: b.basis, basisSubtype: b.basis === 'consent' ? null : `${b.code} — ${b.label}` }));
  };

  const complete = (code: string) => {
    const t = translations[code];
    return !!(t && t.name.trim() && t.description.trim());
  };

  async function save() {
    setBusy(true);
    setError(null);
    try {
      // Only languages with both name and description are sent; a half-typed
      // language would otherwise count as covered and be served.
      const kept: Record<string, Translation> = {};
      for (const [code, t] of Object.entries(translations)) {
        if (!complete(code)) continue;
        kept[code] = {
          name: t.name.trim(), description: t.description.trim(),
          ...(t.dataCategories && t.dataCategories.length === form.dataCategories.length && t.dataCategories.every(Boolean)
            ? { dataCategories: t.dataCategories } : {}),
          ...(form.basis === 'legitimate_use' && t.basisSubtype?.trim() ? { basisSubtype: t.basisSubtype.trim() } : {}),
        };
      }
      const body = {
        name: form.name,
        description: form.description,
        dataCategories: form.dataCategories,
        basis: form.basis,
        basisSubtype: form.basisSubtype || null,
        retentionDays: form.retentionDays || null,
        processors: form.processors,
        requiredForService: form.requiredForService,
        translations: kept,
        childRule: form.childRule ?? 'guardian',
        childExemption: form.childRule === 'exempt' ? (form.childExemption ?? null) : null,
        channels: form.basis === 'consent' ? (form.channels ?? []) : [],
        tracking,
        withdrawalStopHours: stopHours || null,
      };
      if (purpose) {
        const res = await api.put<{ newVersion: boolean; purpose: Purpose }>(`/v1/purposes/${purpose.id}`, body);
        onSaved(res.newVersion ? `Saved as v${res.purpose.currentVersion}.` : 'Saved.');
      } else {
        await api.post('/v1/purposes', body);
        onSaved('Purpose created as a draft.');
      }
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={purpose ? `Edit "${purpose.current.name}"` : 'New purpose'}
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy || !form.name || !form.description || (form.childRule === 'exempt' && !form.childExemption)}>
            {busy ? 'Saving…' : isActive ? `Save as v${(purpose?.currentVersion ?? 1) + 1}` : 'Save'}
          </button>
        </>
      }
    >
      {isActive && (
        <div className="note warn" style={{ marginBottom: 14 }}>
          Active purpose. Changes create <strong>v{(purpose?.currentVersion ?? 1) + 1}</strong>;
          existing consents remain bound to v{purpose?.currentVersion}.
        </div>
      )}

      <Field label="Name">
        <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} />
      </Field>

      <Field label="Description" help="Shown to the person exactly as written here.">
        <textarea value={form.description} onChange={(e) => set('description', e.target.value)} />
      </Field>

      <div className="row">
        <Field label="Data categories" help="Comma separated.">
          <input
            type="text"
            value={form.dataCategories.join(', ')}
            onChange={(e) => set('dataCategories', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))}
          />
        </Field>
        <Field label="Retention (days)" help="Empty: until withdrawn.">
          <input
            type="number" min={1} value={form.retentionDays ?? ''}
            onChange={(e) => set('retentionDays', e.target.value ? Number(e.target.value) : null)}
          />
        </Field>
      </div>

      <Field label="Lawful basis" help={form.basis === 'legitimate_use' ? 'Stated to the person with its ground. No consent is asked.' : undefined}>
        <select value={basisValue} onChange={(e) => pickBasis(e.target.value)}>
          {!basisValue && <option value="">Choose</option>}
          {(bases.data?.bases ?? []).map((b) => (
            <option key={b.code} value={b.code}>{b.code} · {b.label}</option>
          ))}
        </select>
      </Field>

      {form.basis === 'consent' && (
        <Field label="Channels">
          <div className="chan-picks">
            {CHANNEL_OPTIONS.map(([key, label]) => (
              <label key={key}>
                <input
                  type="checkbox"
                  checked={(form.channels ?? []).includes(key)}
                  onChange={(e) => set('channels', e.target.checked
                    ? [...(form.channels ?? []), key]
                    : (form.channels ?? []).filter((c) => c !== key))}
                />
                {label}
              </label>
            ))}
          </div>
        </Field>
      )}

      <Field label="Processors" help="Comma separated.">
        <input
          type="text"
          value={form.processors.join(', ')}
          onChange={(e) => set('processors', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))}
        />
      </Field>

      <div className="row">
        <Field label="Children">
          <select
            value={form.childRule ?? 'guardian'}
            onChange={(e) => {
              const rule = e.target.value as ChildRule;
              setForm((f) => ({ ...f, childRule: rule, childExemption: rule === 'exempt' ? (f.childExemption ?? null) : null }));
            }}
          >
            {CHILD_RULE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        {form.childRule === 'exempt' && (
          <Field label="Fourth Schedule item">
            <select value={form.childExemption ?? ''} onChange={(e) => set('childExemption', e.target.value || null)}>
              {!form.childExemption && <option value="">Choose</option>}
              {CHILD_EXEMPTIONS.map(([code, label]) => <option key={code} value={code}>{code} {label}</option>)}
            </select>
          </Field>
        )}
      </div>

      <div className="row">
        <div className="field">
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
            <input
              type="checkbox" style={{ width: 'auto', marginTop: 2 }}
              checked={tracking}
              onChange={(e) => {
                setTracking(e.target.checked);
                // S.9(3): tracking is not offered to children.
                if (e.target.checked && (form.childRule ?? 'guardian') === 'guardian') setForm((f) => ({ ...f, childRule: 'not_for_children', childExemption: null }));
              }}
            />
            <span>
              Tracking, behavioural monitoring or targeted advertising
              <div className="help" style={{ marginTop: 2 }}>Not offered to children, and no high-sensitivity data items. Not shown to the person; no new version.</div>
            </span>
          </label>
        </div>
        <Field label="Stop processing within (hours)" help="After a withdrawal. Empty: the organisation default. No new version.">
          <input
            type="number" min={1} max={8760} value={stopHours ?? ''}
            onChange={(e) => setStopHours(e.target.value ? Number(e.target.value) : null)}
          />
        </Field>
      </div>

      {form.basis === 'consent' && (
        <div className="field">
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
            <input
              type="checkbox" style={{ width: 'auto', marginTop: 2 }}
              checked={form.requiredForService}
              onChange={(e) => set('requiredForService', e.target.checked)}
            />
            <span>
              Needed to provide the service
              <div className="help" style={{ marginTop: 2 }}>
                The service cannot be delivered if the person says no. Leave unticked for anything optional; an optional purpose must not block access (S.6(1)).
              </div>
            </span>
          </label>
        </div>
      )}

      <h2 className="section" style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span>Other languages</span>
        <span className="small muted" style={{ fontWeight: 400 }}>
          {others.length
            ? `${others.filter(complete).length} of ${others.length} complete`
            : 'Only English offered'}
        </span>
      </h2>

      {others.length > 0 && current && (
        <>
          <div className="tabs" role="tablist" style={{ flexWrap: 'wrap' }}>
            {others.map((code) => (
              <button
                key={code} role="tab" type="button" aria-selected={code === current}
                className={code === current ? 'active' : ''}
                onClick={() => setLang(code)}
                lang={code}
                title={complete(code) ? 'Complete' : 'Not yet translated'}
              >
                {info(code)?.endonym ?? code}
                {!complete(code) && <span className="tab-flag" aria-label="incomplete"> ●</span>}
              </button>
            ))}
          </div>
          {info(current) && !info(current)!.interfacePack && (
            <div className="note warn" style={{ marginBottom: 12 }}>
              {info(current)!.english}: the widget and preference centre do not yet carry their own buttons and labels in this language, so a notice cannot be served in it until that pack is added. The text is kept.
            </div>
          )}
          <div className="row" lang={current}>
            <Field label={`Name · ${info(current)?.endonym ?? current}`}>
              <input type="text" value={translations[current]?.name ?? ''} onChange={(e) => setT(current, { name: e.target.value })} />
            </Field>
          </div>
          <Field label={`Description · ${info(current)?.endonym ?? current}`}>
            <textarea lang={current} value={translations[current]?.description ?? ''} onChange={(e) => setT(current, { description: e.target.value })} />
          </Field>
          <Field
            label={`Data categories · ${info(current)?.endonym ?? current}`}
            help={`Optional. One per English term, in the same order: ${form.dataCategories.join(', ') || 'none yet'}. Common terms are translated automatically when left empty.`}
          >
            <input
              type="text" lang={current}
              value={(translations[current]?.dataCategories ?? []).join(', ')}
              onChange={(e) => setT(current, { dataCategories: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
            />
          </Field>
          {form.basis === 'legitimate_use' && (
            <Field label={`Legal ground · ${info(current)?.endonym ?? current}`} help="Optional. Without it only the clause number is shown, which reads the same in every language.">
              <input type="text" lang={current} value={translations[current]?.basisSubtype ?? ''} onChange={(e) => setT(current, { basisSubtype: e.target.value })} />
            </Field>
          )}
        </>
      )}

      {purpose && (
        <div className="small muted" style={{ marginTop: 12 }}>
          Content hash <Hash value={purpose.current.contentHash} /> · created {date(purpose.current.createdAt)}
        </div>
      )}

      <ErrorNote error={error} />
    </Modal>
  );
}

function RetireDialog({
  purpose, onClose, onDone,
}: { purpose: Purpose; onClose: () => void; onDone: () => void }) {
  const [disposition, setDisposition] = useState<'keep_for_record' | 'processing_stopped'>('processing_stopped');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function retire() {
    setBusy(true);
    try {
      await api.post(`/v1/purposes/${purpose.id}/retire`, { disposition });
      onDone();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Retire "${purpose.current.name}"`}
      description="The purpose is withdrawn from all collection points. Consent records are retained."
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="danger" onClick={retire} disabled={busy}>{busy ? 'Retiring…' : 'Retire purpose'}</button>
        </>
      }
    >
      <div className="field">
        <label>Existing consents</label>
        {([
          ['processing_stopped', 'Stop processing', 'Processing ceases and downstream subscribers are notified.'],
          ['keep_for_record', 'Retain records only', 'Processing already ceased; records retained as evidence.'],
        ] as const).map(([value, title, help]) => (
          <label key={value} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '8px 0', fontWeight: 400 }}>
            <input
              type="radio" name="disposition" value={value} checked={disposition === value}
              style={{ width: 'auto', marginTop: 3 }}
              onChange={() => setDisposition(value)}
            />
            <span>
              <strong>{title}</strong>
              <div className="help">{help}</div>
            </span>
          </label>
        ))}
      </div>
      <ErrorNote error={error} />
    </Modal>
  );
}
